import { useTraduction } from '../i18n/langue';

/**
 * Présentation du produit, sur les écrans d'entrée.
 *
 * Elle s'adresse d'abord au **parent invité** : il n'a rien demandé, il arrive parfois à
 * contrecœur, et il doit comprendre en trois phrases à qui s'adresse l'outil, ce qu'il
 * fait, et pourquoi il le protège lui aussi (docs/01 §4). Le ton est donc symétrique —
 * jamais « pour prouver ce que l'autre a fait », toujours « ce qui est écrit vaut pour
 * les deux ».
 *
 * Le public est nommé explicitement, y compris « quel que soit l'état de vos relations » :
 * un parent qui se reconnaît dans la description n'a pas à se demander si le produit est
 * fait pour sa situation. Les professionnels y figurent aussi — ils arrivent par le même
 * écran, et découvrir qu'on n'est pas au bon endroit après avoir créé un compte est une
 * perte de temps évitable.
 *
 * Aucune promesse juridique n'y figure : Repère produit un historique fiable, il ne
 * délivre pas de valeur probante garantie, et le dire serait faux (docs/05 §2).
 *
 * **Deux longueurs, pour deux publics.** L'argumentaire complet s'adresse à qui n'a pas
 * encore de compte : il doit comprendre à qui l'outil s'adresse et pourquoi il le protège
 * lui aussi. Qui arrive sur la connexion a déjà un compte — lui reservir quatre-vingt-dix
 * mots à chaque visite lui coûte une lecture, tous les jours, pendant des années. La forme
 * courte garde la phrase qui situe le produit, et rien de plus.
 */
export function PresentationRepere({ court = false }: { court?: boolean }) {
  const { messages } = useTraduction();

  if (court) {
    return (
      <div className="presentation">
        <p className="accroche">{messages.entree.presentation.accrocheCourte}</p>
      </div>
    );
  }

  return (
    <div className="presentation">
      <p className="accroche">{messages.entree.presentation.accrocheLongue}</p>
      <p>{messages.entree.presentation.publics}</p>
      <p>{messages.entree.presentation.garanties}</p>
    </div>
  );
}

/**
 * Le public et les garanties — la suite de la présentation longue, à part.
 *
 * Sur téléphone, l'inscription passe désormais par l'accroche courte en tête d'écran (voir
 * `EcranEntree`) : ce que dit `publics` — que le produit s'adresse aux deux parents quel
 * que soit l'état de leurs relations — et `garanties` — que rien n'est modifiable par
 * personne — ne disparaissent pas pour autant. Ils rejoignent la feuille du formulaire,
 * après les champs : c'est là qu'on se demande encore « est-ce fait pour ma situation ? »
 * une fois qu'on s'apprête réellement à créer un compte, pas avant d'avoir vu qu'on le
 * pouvait.
 */
export function DetailsPresentation() {
  const { messages } = useTraduction();

  return (
    <div className="presentation">
      <p>{messages.entree.presentation.publics}</p>
      <p>{messages.entree.presentation.garanties}</p>
    </div>
  );
}
